import { TTicketDetails } from '@daymade/api-contracts';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { EmailConfirmationBanner } from 'components/confirmation/EmailConfirmationBanner';
import ConfirmationDrawCountdown from 'components/confirmation2026/ConfirmationDrawCountdown';
import { ConfirmationLinks } from 'components/confirmation2026/ConfirmationLinks';
import { YourPerks } from 'components/confirmation2026/perks/YourPerks';
import { YourSubscription } from 'components/confirmation2026/YourSubscription';
import AvatarSpeechBubble from 'components/dashboard/your-referrals/AvatarSpeechBubble';
import ImageWithFallback from 'components/ImageWithFallback';
import { AvatarOnRubberRing } from 'components/referrals/2026/AvatarOnRubberRing';
import { CONFIRMATION_ASSET_FOLDER } from 'constants/Confirmation';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { TSubscriptions } from 'stores/UserStore';
import styles from './OrderConfirmationContent.module.css';

const WHATS_NEXT_ITEMS = [
  {
    imageSrc: `${CONFIRMATION_ASSET_FOLDER}/draws.svg`,
    header: (
      <>
        When are <br /> the draws?
      </>
    ),
    description: (
      <>
        Our draws are every Tuesday <br className="desktopOnly" />
        and Friday <br className="mobileOnly" />
        at 5pm - watch <br className="desktopOnly" />
        live on{' '}
        <Link
          href="https://www.instagram.com/daymadeuk/"
          className="textDecorationUnderline"
          target="_blank"
        >
          Instagram
        </Link>{' '}
        on
        <br className="mobileOnly" />
        Fridays
      </>
    ),
  },
  {
    imageSrc: `${CONFIRMATION_ASSET_FOLDER}/results.svg`,
    header: (
      <>
        Where can I find <br />
        the results?
      </>
    ),
    description: (
      <>
        You can find results directly <br className="desktopOnly" />
        after the <br className="mobileOnly" /> draw on our{' '}
        <Link href="/results" className="textDecorationUnderline">
          Results <br className="desktopOnly" />
          page
        </Link>{' '}
        or{' '}
        <Link
          href="https://www.instagram.com/daymadeuk/"
          className="textDecorationUnderline"
          target="_blank"
        >
          Instagram
        </Link>
      </>
    ),
  },
  {
    imageSrc: `${CONFIRMATION_ASSET_FOLDER}/wins.svg`,
    header: (
      <>
        What if <br className="desktopOnly" />I win?
      </>
    ),
    description: (
      <>
        We contact all our winners <br className="desktopOnly" />
        via email <br className="mobileOnly" />
        directly after the <br className="desktopOnly" /> draw.
      </>
    ),
  },
];

export interface OrderConfirmationContentProps {
  currentEmailAddress?: string | null;
  activeSubscription: TSubscriptions[0] | undefined;
  activeWeeklyDrawTickets: TTicketDetails[];
  isInMonthlyRaffle: boolean;
}

const OrderConfirmationContent = observer(
  ({
    currentEmailAddress,
    activeSubscription,
    activeWeeklyDrawTickets,
    isInMonthlyRaffle,
  }: OrderConfirmationContentProps) => {
    const [expanded, setExpanded] = useState(false);
    const isPremiumSubscription =
      (activeSubscription?.drawSchedulePlans.filter(
        (x) =>
          x.drawTypePlan.code === 'PREMIUM' &&
          x.drawTypePlan.drawTypeCode === 'DAYMADEDRAW',
      ).length || 0) > 0;
    const [canShowWinWithFriendsPopup, setCanShowWinWithFriendsPopup] =
      useState(true);
    const uiStore = useUIStore();
    const userStore = useUserStore();

    useEffect(() => {
      uiStore.setZendeskChatBotVisibility('confirmationPage', false);
      return () => {
        uiStore.setZendeskChatBotVisibility('confirmationPage', true);
      };
    }, []);
    const firstDrawDate = activeWeeklyDrawTickets[0]?.draw?.drawDate ?? null;

    return (
      <div
        onClick={expanded ? () => setExpanded(!expanded) : undefined}
        className="daymadeTypographyReset"
        style={{
          width: '100%',
          minHeight: '100vh',
        }}
      >
        {currentEmailAddress && (
          <EmailConfirmationBanner
            emailAddress={currentEmailAddress}
            subscriptionId={activeSubscription?.subscription.id}
          />
        )}
        <AboutUsModule background="white" withNoPadding>
          <div className={styles.welcomeContainer}>
            <div className={styles.welcomeImageContainer}>
              <h3
                className={`aboutUsTypographyHeaderXlarge ${styles.welcomeImageText} mobileOnly`}
              >
                Welcome to
                <br />
                DAYMADE!
              </h3>

              <div className={styles.welcomeImageWrapper}>
                <ImageWithFallback
                  layout="fill"
                  objectFit="cover"
                  src={`${CONFIRMATION_ASSET_FOLDER}/callum-welcome.png`}
                />
              </div>
            </div>

            <div className={styles.welcomeContentOuter}>
              <div className={styles.welcomeContent}>
                <h3
                  className={`aboutUsTypographyHeaderXlargeOnWhite desktopOnly alignLeftOnDesktop`}
                  data-text="Welcome to DAYMADE!"
                >
                  Welcome to DAYMADE!
                </h3>

                <p className="noMargin textAlignCenter alignLeftOnDesktop">
                  You’ve now joined the tribe and are 1 step closer to ticking
                  off your bucket list!
                </p>

                {firstDrawDate && (
                  <ConfirmationDrawCountdown date={firstDrawDate} />
                )}

                {activeSubscription ? (
                  <YourSubscription
                    expanded={expanded}
                    onClick={() => setExpanded(!expanded)}
                    activeTickets={activeWeeklyDrawTickets}
                    activeSubscription={activeSubscription}
                  />
                ) : (
                  <p>
                    Sorry, there was an error displaying your subscription.
                    Please check{' '}
                    <a
                      // Use a non-NextJS link to force a page reload, so that all stores are reloaded
                      href="/account"
                    >
                      your account
                    </a>{' '}
                    for more information.
                  </p>
                )}
              </div>
            </div>
          </div>
        </AboutUsModule>
        <AboutUsModule background="grey">
          <AboutUsSiteContainer>
            <div className={styles.whatsNextContainer}>
              <h3
                data-text="What's next?"
                className="aboutUsTypographyHeaderXlargeOnWhite textAlignCenter"
              >
                What's next?
              </h3>

              <div className={styles.whatsNextTextSectionContainer}>
                {WHATS_NEXT_ITEMS.map((item, index) => (
                  <div className={styles.whatsNextItemContainer} key={index}>
                    <div className={styles.nextStepsIconContainer}>
                      <ImageWithFallback layout="fill" src={item.imageSrc} />
                    </div>
                    <h4 className="aboutUsTypographyHeaderMedium noMargin textAlignCenter">
                      {item.header}
                    </h4>
                    <p className="noMargin textAlignCenter">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="tealGradient" withNoPadding>
          <div className={styles.perksSection}>
            <div
              className={styles.perksImage}
              style={{
                ['--perks-image-filename-mobile' as any]: `url(${CONFIRMATION_ASSET_FOLDER}/perks-mobile.webp)`,
                ['--perks-image-filename-desktop' as any]: `url(${CONFIRMATION_ASSET_FOLDER}/perks-desktop.webp)`,
              }}
            />
            <div className={styles.perksContent}>
              <YourPerks isInMonthlyTestGroup={isInMonthlyRaffle} />
            </div>
          </div>
        </AboutUsModule>
        <AboutUsModule background="white" withNoPadding>
          <ConfirmationLinks />
        </AboutUsModule>
        {isPremiumSubscription && canShowWinWithFriendsPopup && (
          <div className={styles.winWithFriendsContainer}>
            <div className={styles.winWithFriends}>
              <div className={styles.winWithFriendsAvatar}>
                <AvatarOnRubberRing
                  avatarUrl={userStore.currentUserAvatar}
                  color="red"
                />
              </div>
              <div className={styles.winWithFriendsSpeechBubble}>
                <AvatarSpeechBubble
                  backgroundColour="coral"
                  onCloseClicked={() => setCanShowWinWithFriendsPopup(false)}
                >
                  <p className="textAlignLeft textWhite">
                    You can now{' '}
                    <Link
                      href="/referrals"
                      className={styles.winWithFriendsLink}
                    >
                      invite friends
                    </Link>{' '}
                    & win rewards worth up to £5k whenever they win a prize in
                    our weekly draws!
                  </p>
                </AvatarSpeechBubble>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default OrderConfirmationContent;
